// Copyright ©️ 2026 eVolve MEP, LLC

import { type SelectionMouseEvent, MULTI_SELECT_TOOL_NAME } from './types';

const { Autodesk } = window;

class MultiSelectTool implements Autodesk.Viewing.ToolInterface {
  active: boolean;
  isDragging: boolean;
  viewer: Autodesk.Viewing.GuiViewer3D;

  constructor(viewer: Autodesk.Viewing.GuiViewer3D) {
    this.viewer = viewer;

    this.active = false;
    this.isDragging = false;
  }

  /* oxlint-disable no-empty-function */
  register() {}

  /* oxlint-disable no-empty-function */
  deregister() {}

  activate() {
    this.active = true;
  }

  deactivate() {
    this.active = false;
  }

  getName() {
    return MULTI_SELECT_TOOL_NAME;
  }

  getNames() {
    return [MULTI_SELECT_TOOL_NAME];
  }

  getCursor() {
    return 'default';
  }

  update(_timestamp: number) {
    return false;
  }

  // Tools get called in the order of their priority (higher numbers first). Default tools have a priority of 0.
  getPriority() {
    return 98;
  }

  handleSingleClick(event: SelectionMouseEvent, button: number) {
    const isLeftClick = button === 0;
    if (!isLeftClick) return false;

    // Find the element clicked that has been clicked. The result will be null if empty space has been clicked.
    const castResult: { dbId?: unknown } | null = this.viewer.clientToWorld(event.canvasX, event.canvasY);
    const selectedElementId = typeof castResult?.dbId === 'number' ? castResult.dbId : null;

    // Do nothing when empty space is selected.
    if (!selectedElementId) {
      // Return true to stop the event from propagating to other tools (i.e. prevent default selection behavior).
      return true;
    }

    const existingSelection = this.viewer.getSelection();
    const updatedSelection = [...existingSelection];

    const existingIndex = existingSelection.findIndex((elementId) => elementId === selectedElementId);
    const wasPreviouslySelected = existingIndex >= 0;

    // Remove the element if it's already been selected.
    if (wasPreviouslySelected) {
      updatedSelection.splice(existingIndex, 1);
    }
    // Otherwise, add the new element to the selection.
    else {
      updatedSelection.push(selectedElementId);
    }

    // Commit the updated selection.
    this.viewer.select(updatedSelection);

    // Return true to stop the event from propagating to other tools (i.e. prevent default selection behavior).
    return true;
  }
}

class MultiSelectExtension extends Autodesk.Viewing.Extension {
  tool: MultiSelectTool;

  constructor(viewer: Autodesk.Viewing.GuiViewer3D, options: Autodesk.Viewing.ExtensionOptions) {
    super(viewer, options);
    this.tool = new MultiSelectTool(viewer);
  }

  load() {
    this.viewer.toolController.registerTool(this.tool);

    return true;
  }

  unload() {
    this.viewer.toolController.deactivateTool(MULTI_SELECT_TOOL_NAME);
    this.viewer.toolController.deregisterTool(this.tool);

    return true;
  }
}

Autodesk.Viewing.theExtensionManager.registerExtension('MultiSelectExtension', MultiSelectExtension);
